import { createFocusTrap } from 'focus-trap'

const ROOT_SELECTOR = '.js-development-filters'
const DIALOG_SELECTOR = '.js-development-filters-dialog'
const PANEL_SELECTOR = '.js-development-filters-panel'
const OPEN_SELECTOR = '.js-development-filters-open'
const CLOSE_SELECTOR = '.js-development-filters-close'

// Below the `xs` (50rem) breakpoint the filter bar is a modal; at or above it
// renders inline and this controller stays dormant.
const MOBILE_MQ = '(max-width: 49.99rem)'

/**
 * Mobile filters modal.
 * @description Collapses the development filter bar behind a trigger button below the
 * `xs` breakpoint and presents it in a modal dialog. Inert at `xs` and up, where the
 * bar renders inline. The dialog markup and modal semantics (role/aria-modal) are only
 * applied while acting as a modal on mobile.
 */
export const filtersModalInit = () => {
    const root = document.querySelector(ROOT_SELECTOR)
    if (!root) return

    const dialog = root.querySelector(DIALOG_SELECTOR)
    const panel = root.querySelector(PANEL_SELECTOR) || dialog
    const openBtn = root.querySelector(OPEN_SELECTOR)
    const closeBtn = root.querySelector(CLOSE_SELECTOR)
    if (!dialog || !panel || !openBtn) return

    const mobileMq = window.matchMedia(MOBILE_MQ)
    const focusTrap = createFocusTrap(dialog, {
        escapeDeactivates: false,
        allowOutsideClick: true,
        initialFocus: closeBtn || panel,
        fallbackFocus: panel,
    })

    let isOpen = false

    // This same dialog markup is also the always-visible desktop layout, so
    // "hidden from AT" is only ever true while genuinely acting as a closed
    // mobile modal - never a hardcoded default. Needs to run on init too,
    // not just future breakpoint changes, since a page loaded at desktop
    // width would otherwise never get this cleared.
    const syncAriaHidden = () => {
        dialog.setAttribute('aria-hidden', mobileMq.matches && !isOpen ? 'true' : 'false')
    }

    const open = () => {
        if (isOpen || !mobileMq.matches) return
        isOpen = true

        dialog.classList.remove('hidden')
        dialog.classList.add('flex')
        syncAriaHidden()
        // Modal semantics only apply while it's actually behaving as a modal.
        panel.setAttribute('role', 'dialog')
        panel.setAttribute('aria-modal', 'true')
        openBtn.setAttribute('aria-expanded', 'true')
        document.body.classList.add('overflow-hidden')

        focusTrap.activate()
    }

    const close = ({ restoreFocus = true } = {}) => {
        if (!isOpen) return
        isOpen = false

        focusTrap.deactivate()

        dialog.classList.add('hidden')
        dialog.classList.remove('flex')
        syncAriaHidden()
        panel.removeAttribute('role')
        panel.removeAttribute('aria-modal')
        openBtn.setAttribute('aria-expanded', 'false')
        document.body.classList.remove('overflow-hidden')

        if (restoreFocus) openBtn.focus()
    }

    openBtn.addEventListener('click', open)
    closeBtn?.addEventListener('click', () => close())

    // Click on the backdrop (the overlay itself, outside the panel) closes.
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close()
    })

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isOpen) close()
    })

    // Resizing up to the inline layout must leave a clean, open-able state behind.
    mobileMq.addEventListener('change', (event) => {
        if (!event.matches) close({ restoreFocus: false })
        syncAriaHidden()
    })

    syncAriaHidden()
}
